import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { siteInfo } from '../data/mockData';
import ContentCarousel from '../components/ContentCarousel';
import ScrollArrows from '../components/ScrollArrows';
import MoodCheckInCard from '../components/MoodCheckInCard';
import { Sparkles, Brain, Moon, Wind, ChevronRight, Flame, Trophy, Award, AlertCircle, Waves, BookOpen, Play, MessageCircle, Share2, Heart, Headphones, Leaf } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useContent } from '../context/ContentContext';
import Logo from '../components/Logo';
import { getTimeTheme } from '../utils/timeTheme';
import { getDailyManifest } from '../utils/dailyManifest';
import { shareAffirmationImage } from '../utils/shareAffirmationImage';
import AffirmationShareMenu from '../components/AffirmationShareMenu';
import ReflectionCoachOverlay from '../components/ReflectionCoachOverlay';
import CircleDiscoveryCarousel from '../components/CircleDiscoveryCarousel';
import ShareSheet from '../components/ShareSheet';
import NotificationBell from '../components/NotificationBell';
import AffirmationListenButton from '../components/AffirmationListenButton';
import NeedAMomentModal from '../components/NeedAMomentModal';
import ContinueListeningRail from '../components/ContinueListeningRail';
import TodayPersonalized from '../components/TodayPersonalized';
import { LaunchBanner } from '../components/LaunchBanner';
import { InstallGuideCard } from '../components/InstallGuideCard';
import { toast } from 'sonner';

// Deterministic featured-post picker — same post all day, rotates at local midnight.
const pickDailyFeatured = (allPosts) => {
  if (!allPosts || allPosts.length === 0) return null;
  const now = new Date();
  // YYYYMMDD as a stable integer seed for today (local time)
  const seed =
    now.getFullYear() * 10000 +
    (now.getMonth() + 1) * 100 +
    now.getDate();
  // Simple hash for better distribution across the post list
  let h = seed;
  h = ((h << 5) - h + 0x9e3779b9) >>> 0;
  h ^= h >>> 13;
  h = (h * 0x85ebca6b) >>> 0;
  const idx = h % allPosts.length;
  return allPosts[idx];
};

const iconMap = {
  Sparkles: Sparkles,
  Brain: Brain,
  Moon: Moon,
  Wind: Wind,
};

const categoryShortNames = {
  'affirmations': 'Affirm',
  'guided-meditations': 'Meditate',
  'sleep': 'Sleep',
  'anxiety': 'Anxiety',
};

const ReflectionCoachCard = ({ onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    data-testid="reflection-coach-open-btn"
    className="w-full text-left rounded-3xl p-4 flex items-center gap-3 hover:-translate-y-0.5 transition-all active:scale-[0.99]"
    style={{
      background: 'linear-gradient(135deg, #173F35 0%, #245549 100%)',
      border: '1px solid rgba(214,173,96,0.35)',
      boxShadow: '0 10px 30px rgba(23,63,53,0.25)',
      WebkitTapHighlightColor: 'transparent',
      touchAction: 'manipulation',
    }}
  >
    <div
      className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
      style={{
        background: 'linear-gradient(135deg, #D6AD60 0%, #AA8347 100%)',
        boxShadow: '0 4px 12px rgba(214,173,96,0.35)',
      }}
    >
      <Sparkles className="w-5 h-5" style={{ color: '#173F35' }} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] uppercase tracking-[0.24em] font-semibold mb-0.5" style={{ color: '#D6AD60' }}>
        Reflection Coach
      </p>
      <p className="text-sm leading-snug" style={{ color: '#F8F6F0', fontFamily: "'Playfair Display', serif" }}>
        What would you like to talk through today?
      </p>
    </div>
    <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#D6AD60' }} />
  </button>
);


const HomePage = () => {
  const { user, streak, earnedBadgeCount, readPosts, bookmarkedPosts, toggleBookmarkPost } = useAuth();
  const { posts, categories, programs, soundscapes, getPostsByCategory } = useContent();

  // Re-evaluate the time-of-day theme every 5 minutes so the hero card shifts
  // organically as the day moves on.
  const [theme, setTheme] = useState(() => getTimeTheme());
  useEffect(() => {
    const interval = setInterval(() => setTheme(getTimeTheme()), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Random featured post — rotates daily, deterministic so it doesn't flicker.
  const allPosts = useMemo(() => posts, [posts]);
  const [featured, setFeatured] = useState(() => pickDailyFeatured(allPosts));

  // Today's Manifest — deterministic per local calendar day.
  const dailyManifest = useMemo(() => getDailyManifest(), []);
  const [dailyShareOpen, setDailyShareOpen] = useState(false);
  const [sharePost, setSharePost] = useState(null);  // for Recommended-for-You meditation
  const [coachOpen, setCoachOpen] = useState(false);
  const [needMomentOpen, setNeedMomentOpen] = useState(false);

  const openDailyShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDailyShareOpen(true);
  };
  useEffect(() => {
    // Check every 10 minutes whether the calendar day rolled over.
    const interval = setInterval(() => {
      const next = pickDailyFeatured(allPosts);
      setFeatured((curr) => (curr?.id !== next?.id ? next : curr));
    }, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [allPosts]);

  // Time-of-day greeting used by the new hero.
  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 5) return 'Rest well';
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    if (h < 21) return 'Good evening';
    return 'Peaceful night';
  }, []);

  // Daily community question — mirrored from /api/community/today so
  // returning users see the day's prompt above the fold without opening
  // the Community tab.
  const [todayPrompt, setTodayPrompt] = useState('');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/community/today`);
        const body = await res.json();
        if (!cancelled) setTodayPrompt(body?.prompt || '');
      } catch { /* silently ignore — Home still works without the prompt */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Bookmark ID for today's affirmation — piggybacks on the existing
  // bookmarked-posts collection so persistence + badges work automatically.
  const dailyBookmarkId = dailyManifest
    ? `manifest-${dailyManifest.theme.id}-${dailyManifest.index}`
    : null;
  const isSaved = dailyBookmarkId && bookmarkedPosts?.includes(dailyBookmarkId);

  const handleSave = useCallback(async () => {
    if (!dailyBookmarkId) return;
    if (!user) {
      toast('Sign in to save affirmations');
      return;
    }
    await toggleBookmarkPost(dailyBookmarkId);
    toast.success(isSaved ? 'Removed from saved' : 'Saved for later');
  }, [dailyBookmarkId, isSaved, toggleBookmarkPost, user]);

  // Pick a recommended guided-meditation post — deterministic per day, and
  // prefer one the user has not read yet.
  const recommended = useMemo(() => {
    if (!allPosts?.length) return null;
    const guided = allPosts.filter((p) => p.categoryId === 'guided-meditations');
    const pool = guided.length ? guided : allPosts;
    const unread = pool.filter((p) => !(readPosts || []).includes(p.id));
    const source = unread.length ? unread : pool;
    return pickDailyFeatured(source);
  }, [allPosts, readPosts]);

  // Continue Your Journey — pick the program with the most progress that
  // isn't already 100% complete. Progress is computed from the overlap
  // between the user's `readPosts` and the program's post-kind session refs.
  const journey = useMemo(() => {
    if (!programs?.length) return null;
    const rp = readPosts || [];
    const scored = programs.map((p) => {
      const postRefs = p.sessions.filter((s) => s.kind === 'post').map((s) => s.ref);
      const done = postRefs.filter((r) => rp.includes(r)).length;
      const total = postRefs.length || p.sessions.length;
      const pct = total ? Math.round((done / total) * 100) : 0;
      return { program: p, pct };
    });
    // Prefer in-progress (>0, <100), then any that has started, then default.
    const inProgress = scored.find((s) => s.pct > 0 && s.pct < 100);
    if (inProgress) return inProgress;
    const started = scored.find((s) => s.pct > 0);
    if (started) return started;
    return scored[0] || null;
  }, [programs, readPosts]);

  return (
    <div className="pb-4">
      {/* Hero — soft-white greeting on the dark forest shell, gold eyebrow.
          For logged-in users the greeting lives inside <TodayPersonalized/>
          below, so we hide the second title here to avoid duplication. */}
      <div className="relative px-5 pt-6 pb-4 animate-fade-in">
        <div className="flex items-start justify-between gap-3 mb-2">
          <p className="rbr-eyebrow" style={{ color: '#D6AD60' }}>
            Today · {new Date().toLocaleDateString('en-US', { weekday: 'long' })}
          </p>
          <NotificationBell tone="dark" />
        </div>
      </div>

      <LaunchBanner placement="today" />
      <InstallGuideCard />

      {/* Today rails — same for members and visitors (visitors get a generic
          greeting; mood check-in / Continue Listening need a profile and hide themselves). */}
      <TodayPersonalized coachSlot={<ReflectionCoachCard onOpen={() => setCoachOpen(true)} />} />

      {/* Today's Question, Today's Manifestation, duplicate Reflection Coach,
          and the mid-page mini-nav strip were removed on user's request.
          Reflection Coach lives above the fold now; primary navigation
          (Home/Explore/Breathe/You) remains available in the BottomNav. */}

      {/* Circle Discovery — surfaces circles the user hasn't joined yet with
          today's rotating card so their "season of life" is one tap away. */}
      <CircleDiscoveryCarousel />

      {/* Mood check-in is now rendered inside <TodayPersonalized/> for logged-in
          users so it feeds the personalization engine. Standalone card removed. */}

      {/* Quick Reset — three low-friction "here right now" tiles */}
      <div className="px-5 mb-5 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
        <p className="rbr-eyebrow mb-3">Quick Reset</p>
        <div className="grid grid-cols-3 gap-3">
          <Link
            to="/breathing"
            data-testid="quick-reset-breathe"
            className="flex flex-col items-center gap-1.5 p-3.5 rounded-2xl bg-[#1F4C40] border border-[#2E6353] hover:border-[#D6AD60]/60 hover:shadow-md hover:-translate-y-0.5 transition-all group"
          >
            <div className="w-11 h-11 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center group-hover:scale-105 transition-transform">
              <Wind className="w-5 h-5 text-emerald-600" />
            </div>
            <p className="text-xs font-semibold text-[#F8F6F0]">Breathe</p>
            <p className="text-[10px] text-[#A3BEAF]">1 min</p>
          </Link>
          <Link
            to="/category/guided-meditations"
            data-testid="quick-reset-meditate"
            className="flex flex-col items-center gap-1.5 p-3.5 rounded-2xl bg-[#1F4C40] border border-[#2E6353] hover:border-[#D6AD60]/60 hover:shadow-md hover:-translate-y-0.5 transition-all group"
          >
            <div className="w-11 h-11 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center group-hover:scale-105 transition-transform">
              <Headphones className="w-5 h-5 text-indigo-600" />
            </div>
            <p className="text-xs font-semibold text-[#F8F6F0]">Meditate</p>
            <p className="text-[10px] text-[#A3BEAF]">5 min</p>
          </Link>
          <Link
            to="/daily"
            data-testid="quick-reset-affirm"
            className="flex flex-col items-center gap-1.5 p-3.5 rounded-2xl bg-[#1F4C40] border border-[#2E6353] hover:border-[#D6AD60]/60 hover:shadow-md hover:-translate-y-0.5 transition-all group"
          >
            <div className="w-11 h-11 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center group-hover:scale-105 transition-transform">
              <Sparkles className="w-5 h-5 text-amber-600" />
            </div>
            <p className="text-xs font-semibold text-[#F8F6F0]">Affirm</p>
            <p className="text-[10px] text-[#A3BEAF]">Today</p>
          </Link>
        </div>
      </div>

      {/* Legacy single-spotlight "Recommended for You" removed — the backend rail
          inside <TodayPersonalized/> now serves members and visitors alike. */}

      {/* Continue Your Journey — highlight the program the user is furthest
          into (but not yet complete). Progress is computed live from readPosts. */}
      {journey && journey.program && (
        <div className="px-5 mb-6 animate-fade-in-up" style={{ animationDelay: '0.14s' }}>
          <p className="rbr-eyebrow mb-3">Continue Your Journey</p>
          <Link
            to={`/programs/${journey.program.id}`}
            data-testid="home-journey"
            className="block bg-[#1F4C40] border border-[#2E6353] rounded-2xl p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-[#F8F6F0] truncate pr-3" data-testid="home-journey-name">
                {journey.program.name}
              </p>
              <p
                className="text-xs font-semibold flex-shrink-0"
                style={{ color: journey.program.accent || '#D6AD60' }}
                data-testid="home-journey-pct"
              >
                {journey.pct}%
              </p>
            </div>
            <div className="h-2 w-full rounded-full bg-[#E8DDCB] overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${journey.pct}%`,
                  background: `linear-gradient(90deg, ${journey.program.accent || '#D6AD60'}, #d4a952)`,
                }}
              />
            </div>
          </Link>
        </div>
      )}

      {/* NOTE: Category quick-links grid (Affirm / Meditate / Sleep / Anxiety /
          Manifest / Breathe) was removed from Home when the 4-tab nav shipped.
          All that content now lives on the Explore tab. */}

      {/* NOTE: The old photo-hero "Today's Manifest" block was replaced by
          the Today's Reflection card above. The "Read all 30 · <theme>" link
          in that card is the way into the theme detail page. */}

      {/* Streak & Badge Widget (logged-in users) */}
      {user && (
        <div className="px-5 mb-6 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <Link to="/profile" className="block bg-[#1F4C40] border border-[#2E6353] rounded-2xl p-4 shadow-md hover:border-[#D6AD60]/30 hover:shadow-lg transition-all duration-300 group">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 rounded-xl bg-[#E8DDCB] flex items-center justify-center">
                  <Flame className={`w-5 h-5 ${streak.current > 0 ? 'text-orange-400' : 'text-[#A3BEAF]'}`} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#F8F6F0]">
                    {streak.current > 0 ? `${streak.current} day streak` : 'Start your streak'}
                  </p>
                  <p className="text-[10px] text-[#A3BEAF]">
                    {readPosts.length} reflections read · {earnedBadgeCount} badges earned
                  </p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-[#A3BEAF] group-hover:text-[#D6AD60] group-hover:translate-x-0.5 transition-all duration-300" />
            </div>
          </Link>
        </div>
      )}

      {/* Sign up prompt (non-logged users) */}
      {!user && (
        <div className="px-5 mb-6 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <Link to="/auth" className="block bg-[#1F4C40] border border-[#D6AD60]/20 rounded-2xl p-4 shadow-md hover:border-[#D6AD60]/35 hover:shadow-lg transition-all duration-300 group">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 rounded-xl bg-[#E8DDCB] flex items-center justify-center">
                  <Award className="w-5 h-5 text-[#D6AD60]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#F8F6F0]">
                    Create your profile
                  </p>
                  <p className="text-[10px] text-[#A3BEAF]">
                    Track streaks, earn badges, save favorites
                  </p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-[#A3BEAF] group-hover:text-[#D6AD60] group-hover:translate-x-0.5 transition-all duration-300" />
            </div>
          </Link>
        </div>
      )}

      {/* Daily Mood Check-in, SOS, and Reflection Coach moved to the top
          of the page (right under the hero) so they're the first things
          the user sees. Featured post follows. */}

      {/* Featured Post section removed per redesign — the daily reset arc
          (Reflection → Mood → Quick Reset → Recommended → Journey) is the
          primary Home experience now. */}

      {/* NOTE: Category content rows (Affirmations / Guided Meditations /
          Sleep / Anxiety), Programs, and Soundscapes carousels all moved
          to the new Explore tab. Home stays focused on the daily reset
          arc: reflection → mood → quick reset → recommended → journey. */}

      {/* Footer */}
      <div className="px-5 py-8 mt-4 border-t border-stone-100">
        <div className="flex flex-col items-center">
          <Logo size={64} className="mb-3 shadow-md rounded-full ring-2 ring-[#D6AD60]/40" />
          <h3
            className="text-base font-semibold text-[#F8F6F0] mb-0.5"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {siteInfo.name}
          </h3>
          <div className="w-12 h-px bg-gradient-to-r from-transparent via-[#D6AD60] to-transparent mb-3" />
          <p className="text-xs text-[#A3BEAF] mb-4 text-center max-w-xs">
            {siteInfo.description}
          </p>
          <div className="flex items-center gap-5 mb-4">
            <a href={siteInfo.social.facebook} className="text-[#A3BEAF] hover:text-[#D6AD60] transition-colors duration-300">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a href={siteInfo.social.instagram} className="text-[#A3BEAF] hover:text-[#D6AD60] transition-colors duration-300">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
            </a>
            <a href={siteInfo.social.twitter} className="text-[#A3BEAF] hover:text-[#D6AD60] transition-colors duration-300">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
          </div>
          <p className="text-xs text-[#7A9B8C]">
            Copyright &copy; 2025, All rights reserved.
          </p>
        </div>
      </div>

      {/* Reflection Coach overlay — controlled by the CTA card near the top
          of the page. Ephemeral chat (no conversation persistence). */}
      <ReflectionCoachOverlay open={coachOpen} onClose={() => setCoachOpen(false)} />
      <NeedAMomentModal open={needMomentOpen} onClose={() => setNeedMomentOpen(false)} />

      {/* Share sheet for the daily Recommended-for-You meditation card. */}
      <ShareSheet
        open={!!sharePost}
        onClose={() => setSharePost(null)}
        content={sharePost ? {
          kind: 'post',
          id: sharePost.id,
          title: sharePost.title,
          text: `"${sharePost.title}" — a reflection on Reflections by Rowdy`,
          url: typeof window !== 'undefined' ? `${window.location.origin}/post/${sharePost.id}` : '',
          recommendable: true,
        } : undefined}
      />

      {/* SOS + Reflection Coach quick actions were moved off the Home page
          in the redesign. To bring either back, wire buttons that toggle
          local state and mount <SOSModal /> / <ReflectionCoachOverlay />
          from './components/…'. */}
    </div>
  );
};

export default HomePage;
